import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import franchiseService from '../services/franchiseService'
import './FranchiseDashboard.css' // Reusing dashboard styles + specific ones

const FranchiseMenu = () => {
    const navigate = useNavigate()
    const [items, setItems] = useState([])
    const [categories, setCategories] = useState([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')
    const [showAddModal, setShowAddModal] = useState(false)
    const [showCategoryModal, setShowCategoryModal] = useState(false)
    const [newItem, setNewItem] = useState({ name: '', price: '', categoryId: '', description: '', imageUrl: '' })
    const [newCategory, setNewCategory] = useState({ name: '', displayOrder: 0 })

    // Load menu items and categories
    useEffect(() => {
        fetchMenuData()
    }, [])

    const fetchMenuData = async () => {
        setLoading(true)
        setError('')
        try {
            const [itemsResponse, categoriesResponse] = await Promise.all([
                franchiseService.getMenuItems(),
                franchiseService.getCategories()
            ])

            if (itemsResponse.success) {
                setItems(itemsResponse.data || [])
            }

            if (categoriesResponse.success) {
                setCategories(categoriesResponse.data || [])
            }
        } catch (err) {
            console.error('Error fetching menu data:', err)
            setError(err.message || 'Failed to load menu items')
        } finally {
            setLoading(false)
        }
    }

    const handleAddItem = async (e) => {
        e.preventDefault()
        setError('')
        try {
            const itemData = {
                name: newItem.name,
                price: parseFloat(newItem.price),
                categoryId: newItem.categoryId || null,
                description: newItem.description || '',
                imageUrl: newItem.imageUrl || '',
                isAvailable: true
            }

            const response = await franchiseService.createMenuItem(itemData)
            if (response.success) {
                await fetchMenuData() // Refresh the list
                setShowAddModal(false)
                setNewItem({ name: '', price: '', categoryId: '', description: '', imageUrl: '' })
            }
        } catch (err) {
            setError(err.message || 'Failed to add menu item')
        }
    }

    const handleDeleteItem = async (id) => {
        if (!window.confirm('Are you sure you want to delete this menu item? This action cannot be undone.')) {
            return
        }

        setError('')
        try {
            const response = await franchiseService.deleteMenuItem(id)
            if (response.success) {
                await fetchMenuData() // Refresh the list
            }
        } catch (err) {
            setError(err.message || 'Failed to delete menu item')
        }
    }

    const handleToggleAvailability = async (item) => {
        setError('')
        try {
            const response = await franchiseService.updateMenuItem(item.id, {
                isAvailable: !item.is_available
            })
            if (response.success) {
                await fetchMenuData() // Refresh the list
            }
        } catch (err) {
            setError(err.message || 'Failed to update menu item')
        }
    }

    const handleCreateCategory = async (e) => {
        e.preventDefault()
        setError('')
        try {
            const categoryData = {
                name: newCategory.name.trim(),
                displayOrder: parseInt(newCategory.displayOrder) || 0
            }

            if (!categoryData.name) {
                setError('Category name is required')
                return
            }

            const response = await franchiseService.createCategory(categoryData)
            if (response.success) {
                await fetchMenuData() // Refresh categories
                setShowCategoryModal(false)
                setNewCategory({ name: '', displayOrder: 0 })
            }
        } catch (err) {
            setError(err.message || 'Failed to create category')
        }
    }

    return (
        <div className="franchise-dashboard-container">
            <div className="page-header">
                <div className="page-header-left">
                    <button
                        className="btn-secondary"
                        onClick={() => navigate('/franchise/dashboard')}
                    >
                        Back
                    </button>
                    <h1>Manage Menu</h1>
                </div>
                <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                    <button className="btn-secondary" onClick={() => setShowCategoryModal(true)}>
                        Add Category
                    </button>
                    <button className="btn-primary" onClick={() => setShowAddModal(true)}>+ Add Item</button>
                </div>
            </div>

            {error && (
                <div className="alert alert-error" style={{ marginBottom: '1.5rem' }}>
                    {error}
                </div>
            )}

            {/* Categories Section */}
            {!loading && categories.length > 0 && (
                <div className="card" style={{ marginBottom: '2rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                        <h3 style={{ margin: 0, fontSize: '1.25rem' }}>Categories</h3>
                        <button
                            className="btn-secondary"
                            onClick={() => setShowCategoryModal(true)}
                            style={{ padding: '0.5rem 1rem', fontSize: '0.9rem' }}
                        >
                            + Add Category
                        </button>
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem' }}>
                        {categories.map(category => (
                            <span
                                key={category.id}
                                className="category-tag"
                                style={{
                                    padding: '0.5rem 1rem',
                                    background: 'rgba(129, 140, 248, 0.2)',
                                    color: '#a5b4fc',
                                    border: '1px solid rgba(129, 140, 248, 0.3)',
                                    borderRadius: '0.5rem',
                                    fontSize: '0.9rem',
                                    fontWeight: '500'
                                }}
                            >
                                {category.name}
                                {category.display_order !== undefined && category.display_order > 0 && (
                                    <span style={{ marginLeft: '0.5rem', opacity: 0.7, fontSize: '0.8rem' }}>
                                        (#{category.display_order})
                                    </span>
                                )}
                            </span>
                        ))}
                    </div>
                </div>
            )}

            {loading ? (
                <div className="empty-state">
                    <div className="spinner" style={{ margin: '2rem auto' }}></div>
                    <p>Loading menu items...</p>
                </div>
            ) : items.length === 0 ? (
                <div className="empty-state">
                    <p>No menu items yet. Add your first item to get started.</p>
                </div>
            ) : (
                <div className="menu-grid">
                    {items.map(item => (
                        <div key={item.id} className={`menu-item-card ${!item.is_available ? 'unavailable' : ''}`}>
                            <div className="item-info">
                                <div className="item-header">
                                    <h3>{item.name}</h3>
                                    <span className="price">₹{item.price}</span>
                                </div>
                                {item.category_name && (
                                    <span className="category-tag">{item.category_name}</span>
                                )}
                                {item.description && <p>{item.description}</p>}
                                {!item.is_available && (
                                    <span className="badge badge-warning" style={{ marginTop: '0.5rem', display: 'inline-block' }}>
                                        Not Available
                                    </span>
                                )}
                            </div>
                            <div className="item-actions">
                                <button
                                    className={`btn-icon ${!item.is_available ? 'available' : 'unavailable'}`}
                                    title={item.is_available ? 'Make Unavailable' : 'Make Available'}
                                    onClick={() => handleToggleAvailability(item)}
                                >
                                    {item.is_available ? '👁️' : '👁️‍🗨️'}
                                </button>
                                <button
                                    className="btn-icon delete"
                                    title="Delete"
                                    onClick={() => handleDeleteItem(item.id)}
                                >
                                    🗑️
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {
                showAddModal && (
                    <div className="modal-overlay">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h2>Add Menu Item</h2>
                                <button className="modal-close" onClick={() => setShowAddModal(false)}>×</button>
                            </div>
                            <form onSubmit={handleAddItem}>
                                <div className="form-group">
                                    <label>Item Name</label>
                                    <input
                                        placeholder="e.g. Cheese Burger"
                                        value={newItem.name}
                                        onChange={e => setNewItem({ ...newItem, name: e.target.value })}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Price (₹)</label>
                                    <input
                                        placeholder="0.00"
                                        type="number"
                                        value={newItem.price}
                                        onChange={e => setNewItem({ ...newItem, price: e.target.value })}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Category (Optional)</label>
                                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                                        <select
                                            value={newItem.categoryId}
                                            onChange={e => setNewItem({ ...newItem, categoryId: e.target.value })}
                                            style={{ flex: 1 }}
                                        >
                                            <option value="">No Category</option>
                                            {categories.map(c => (
                                                <option key={c.id} value={c.id}>{c.name}</option>
                                            ))}
                                        </select>
                                        <button
                                            type="button"
                                            className="btn-secondary"
                                            onClick={() => {
                                                setShowAddModal(false)
                                                setShowCategoryModal(true)
                                            }}
                                            style={{ padding: '0.5rem 1rem', whiteSpace: 'nowrap' }}
                                            title="Create New Category"
                                        >
                                            + New
                                        </button>
                                    </div>
                                </div>
                                <div className="form-group">
                                    <label>Image URL (Optional)</label>
                                    <input
                                        type="url"
                                        placeholder="https://example.com/image.jpg"
                                        value={newItem.imageUrl}
                                        onChange={e => setNewItem({ ...newItem, imageUrl: e.target.value })}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Description</label>
                                    <textarea
                                        placeholder="Describe the item..."
                                        value={newItem.description}
                                        onChange={e => setNewItem({ ...newItem, description: e.target.value })}
                                        rows="3"
                                    />
                                </div>
                                <div className="modal-actions">
                                    <button type="button" className="btn-secondary" onClick={() => setShowAddModal(false)}>Cancel</button>
                                    <button type="submit" className="btn-primary">Save Item</button>
                                </div>
                            </form>
                        </div>
                    </div>
                )
            }

            {/* Add Category Modal */}
            {
                showCategoryModal && (
                    <div className="modal-overlay" onClick={() => setShowCategoryModal(false)}>
                        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-header">
                                <h2>Add Category</h2>
                                <button className="modal-close" onClick={() => setShowCategoryModal(false)}>×</button>
                            </div>
                            <form onSubmit={handleCreateCategory}>
                                {error && (
                                    <div className="alert alert-error" style={{ marginBottom: '1rem', padding: '0.75rem', borderRadius: '0.5rem', background: 'rgba(239, 68, 68, 0.1)', color: '#fca5a5', border: '1px solid rgba(239, 68, 68, 0.3)' }}>
                                        ⚠️ {error}
                                    </div>
                                )}
                                <div className="form-group">
                                    <label>Category Name <span style={{ color: '#ef4444' }}>*</span></label>
                                    <input
                                        type="text"
                                        placeholder="e.g. Burgers, Drinks, Desserts"
                                        value={newCategory.name}
                                        onChange={e => setNewCategory({ ...newCategory, name: e.target.value })}
                                        required
                                        autoFocus
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Display Order (Optional)</label>
                                    <input
                                        type="number"
                                        placeholder="0"
                                        value={newCategory.displayOrder}
                                        onChange={e => setNewCategory({ ...newCategory, displayOrder: e.target.value })}
                                        min="0"
                                    />
                                    <small style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', marginTop: '0.25rem', display: 'block' }}>
                                        Lower numbers appear first in the menu
                                    </small>
                                </div>
                                <div className="modal-actions">
                                    <button type="button" className="btn-secondary" onClick={() => setShowCategoryModal(false)}>Cancel</button>
                                    <button type="submit" className="btn-primary">Create Category</button>
                                </div>
                            </form>
                        </div>
                    </div>
                )
            }
        </div>
    )
}

export default FranchiseMenu
